import { renderEngineLoop, type EngineProfile } from './engineVoice';

/**
 * Small synthesized sounds (Web Audio): the mower itself, grass rustle, the bike horn, the turn
 * tick, and the little moments (fuel glugging in, a molehill flattened, a flat tire, a tree
 * creaking and falling, sticks snapping, a hive's swarm, the engine sputtering out, the mower
 * coming apart, meadow grass growing back). Synthesized so they follow the game exactly (engine pitch tracks speed) and carry no
 * licensing questions. Everything is deliberately quiet: the game should feel laid back.
 */

/** The reel mower's whirr, or an engine mower's profile. */
export type EngineKind = 'reel' | EngineProfile;

/** Engine loudness: about -29 dBFS RMS, a little above the ambient bed (about -36). */
export const ENGINE_GAIN = 0.17;

export class Synth {
  private readonly noise: AudioBuffer;
  private engine: { stop: () => void; setSpeed: (tilesPerSec: number) => void } | null = null;
  private lastRustle = Number.NEGATIVE_INFINITY;
  private lastBaa = Number.NEGATIVE_INFINITY;
  private lastSnap = Number.NEGATIVE_INFINITY;
  private readonly engineBuffers = new Map<EngineProfile, AudioBuffer>();

  constructor(
    private readonly ctx: AudioContext,
    private readonly out: AudioNode,
  ) {
    // Two seconds of white noise, shared by every noisy sound.
    this.noise = ctx.createBuffer(1, ctx.sampleRate * 2, ctx.sampleRate);
    const data = this.noise.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
  }

  /**
   * Starts the mower's running sound (replacing any previous one). `normalSpeed` is the mower's
   * usual tiles/second; the engine sound is tuned to it and shifts as the actual speed differs.
   */
  startEngine(kind: EngineKind, normalSpeed: number, tilesPerSec: number): void {
    this.stopEngine();
    this.engine =
      kind === 'reel' ? this.reel(tilesPerSec) : this.motor(kind, normalSpeed, tilesPerSec);
  }

  setEngineSpeed(tilesPerSec: number): void {
    this.engine?.setSpeed(tilesPerSec);
  }

  stopEngine(): void {
    this.engine?.stop();
    this.engine = null;
  }

  /** A reel mower: soft, airy whirr pulsing with the blades. */
  private reel(tilesPerSec: number) {
    const { ctx } = this;
    const src = this.noiseSource(true);
    const band = ctx.createBiquadFilter();
    band.type = 'bandpass';
    band.Q.value = 1.2;
    // Eases in (a sound that starts at full level clicks).
    const amp = ctx.createGain();
    amp.gain.setValueAtTime(0, ctx.currentTime);
    amp.gain.setTargetAtTime(0.022, ctx.currentTime, 0.06);
    const lfo = ctx.createOscillator();
    const lfoDepth = ctx.createGain();
    lfoDepth.gain.setValueAtTime(0, ctx.currentTime);
    lfoDepth.gain.setTargetAtTime(0.016, ctx.currentTime, 0.06);
    lfo.connect(lfoDepth).connect(amp.gain);
    src.connect(band).connect(amp).connect(this.out);
    const setSpeed = (s: number) => {
      const t = ctx.currentTime;
      band.frequency.setTargetAtTime(1800 + s * 250, t, 0.1);
      lfo.frequency.setTargetAtTime(3 + s * 3.5, t, 0.1);
    };
    setSpeed(tilesPerSec);
    src.start();
    lfo.start();
    return { setSpeed, stop: () => this.fadeOut(amp, [src, lfo]) };
  }

  /** An engine mower: a looped train of exhaust thumps whose rate follows speed. */
  private motor(profile: EngineProfile, normalSpeed: number, tilesPerSec: number) {
    const { ctx } = this;
    let buffer = this.engineBuffers.get(profile);
    if (!buffer) {
      const samples = renderEngineLoop(ctx.sampleRate, profile);
      buffer = ctx.createBuffer(1, samples.length, ctx.sampleRate);
      buffer.copyToChannel(samples, 0);
      this.engineBuffers.set(profile, buffer);
    }
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    src.loop = true;
    // Rounds off the crackle so it stays a soft rumble.
    const low = ctx.createBiquadFilter();
    low.type = 'lowpass';
    low.frequency.value = 1500;
    low.Q.value = 0.5;
    const amp = ctx.createGain();
    amp.gain.setValueAtTime(0, ctx.currentTime);
    amp.gain.setTargetAtTime(ENGINE_GAIN, ctx.currentTime, 0.08);
    src.connect(low).connect(amp).connect(this.out);
    const setSpeed = (s: number) => {
      // Slower (mud, low gear) labours the engine down; never so far it stops sounding like one.
      const rate = Math.min(1.35, Math.max(0.75, s / normalSpeed));
      src.playbackRate.setTargetAtTime(rate, ctx.currentTime, 0.25);
    };
    setSpeed(tilesPerSec);
    // Start at a random point so restarts do not always begin on the same thump.
    src.start(ctx.currentTime, Math.random() * buffer.duration);
    return { setSpeed, stop: () => this.fadeOut(amp, [src]) };
  }

  /** A faint swish of cut grass; rate-limited so mowing never becomes a buzz. */
  rustle(): void {
    const now = this.ctx.currentTime;
    if (now - this.lastRustle < 0.09) return;
    this.lastRustle = now;
    const src = this.noiseSource(false);
    src.playbackRate.value = 0.8 + Math.random() * 0.4;
    const high = this.ctx.createBiquadFilter();
    high.type = 'highpass';
    high.frequency.value = 2800;
    const amp = this.envelope(0.012, 0.005, 0.07);
    src.connect(high).connect(amp).connect(this.out);
    src.start(now, Math.random(), 0.1);
  }

  /** A ceramic crunch: a garden gnome meeting the mower. */
  crunch(): void {
    const now = this.ctx.currentTime;
    const src = this.noiseSource(false);
    src.playbackRate.value = 1.4;
    const high = this.ctx.createBiquadFilter();
    high.type = 'bandpass';
    high.frequency.setValueAtTime(2400, now);
    high.frequency.exponentialRampToValueAtTime(900, now + 0.12);
    high.Q.value = 0.8;
    const amp = this.envelope(0.09, 0.003, 0.14);
    src.connect(high).connect(amp).connect(this.out);
    src.start(now, Math.random(), 0.18);
  }

  /** A soft, low squish: the mower going through a flower bed. */
  squish(): void {
    const now = this.ctx.currentTime;
    const src = this.noiseSource(false);
    src.playbackRate.value = 0.5;
    const low = this.ctx.createBiquadFilter();
    low.type = 'lowpass';
    low.frequency.setValueAtTime(900, now);
    low.frequency.exponentialRampToValueAtTime(220, now + 0.16);
    const amp = this.envelope(0.05, 0.01, 0.16);
    src.connect(low).connect(amp).connect(this.out);
    src.start(now, Math.random(), 0.2);
  }

  /**
   * A referee's whistle, soft: trilled peeps of these lengths (seconds). Two short ones by
   * default (a stray ball is coming); mower soccer's kick-off and halftime have their own.
   */
  whistle(peeps: readonly number[] = [0.09, 0.16]): void {
    let at = this.ctx.currentTime;
    for (const len of peeps) {
      const osc = this.ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = 2300;
      // The pea's trill.
      const lfo = this.ctx.createOscillator();
      lfo.frequency.value = 38;
      const depth = this.ctx.createGain();
      depth.gain.value = 140;
      lfo.connect(depth).connect(osc.frequency);
      const amp = this.envelope(0.025, 0.005, len, at);
      osc.connect(amp).connect(this.out);
      for (const o of [osc, lfo]) {
        o.start(at);
        o.stop(at + len + 0.02);
      }
      at += len + 0.05;
    }
  }

  /** Mower soccer: a soft, round thump as the mower knocks the ball on (a squirt, softer). */
  kick(soft = false): void {
    const now = this.ctx.currentTime;
    const osc = this.ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(150, now);
    osc.frequency.exponentialRampToValueAtTime(55, now + 0.12);
    const amp = this.envelope(soft ? 0.05 : 0.09, 0.004, 0.14);
    osc.connect(amp).connect(this.out);
    osc.start(now);
    osc.stop(now + 0.18);
    // The leather's slap: a short tap of muffled noise.
    const src = this.noiseSource(false);
    const low = this.ctx.createBiquadFilter();
    low.type = 'lowpass';
    low.frequency.value = 1400;
    const tap = this.envelope(soft ? 0.015 : 0.03, 0.002, 0.04);
    src.connect(low).connect(tap).connect(this.out);
    src.start(now, Math.random(), 0.06);
  }

  /**
   * Mower soccer: the crowd's roar for a goal, far off and soft: a swell of voices (banded noise,
   * gently wavering) that rises fast and dies away over a couple of seconds.
   */
  roar(): void {
    const now = this.ctx.currentTime;
    const src = this.noiseSource(true);
    const voices = this.ctx.createBiquadFilter();
    voices.type = 'bandpass';
    voices.frequency.value = 850;
    voices.Q.value = 0.7;
    const amp = this.ctx.createGain();
    amp.gain.setValueAtTime(0, now);
    amp.gain.linearRampToValueAtTime(0.05, now + 0.3);
    amp.gain.setTargetAtTime(0.035, now + 0.3, 0.25);
    amp.gain.setTargetAtTime(0, now + 1, 0.45);
    // The crowd's waver: a slow wobble in the swell.
    const lfo = this.ctx.createOscillator();
    lfo.frequency.value = 5;
    const depth = this.ctx.createGain();
    depth.gain.value = 0.008;
    lfo.connect(depth).connect(amp.gain);
    src.connect(voices).connect(amp).connect(this.out);
    src.start(now, Math.random());
    lfo.start(now);
    for (const o of [src, lfo]) o.stop(now + 3.2);
  }

  /** "Toot-toot": a friendly two-tone bike horn. */
  horn(): void {
    const now = this.ctx.currentTime;
    for (const [i, freq] of [523, 659].entries()) {
      const osc = this.ctx.createOscillator();
      osc.type = 'square';
      osc.frequency.value = freq;
      const low = this.ctx.createBiquadFilter();
      low.type = 'lowpass';
      low.frequency.value = 1600;
      const amp = this.envelope(0.035, 0.01, 0.11, now + i * 0.14);
      osc.connect(low).connect(amp).connect(this.out);
      osc.start(now + i * 0.14);
      osc.stop(now + i * 0.14 + 0.15);
    }
  }

  /** A soft, wobbly "baa": a sheep being nudged along. Rate-limited. */
  baa(): void {
    const now = this.ctx.currentTime;
    if (now - this.lastBaa < 0.7) return;
    this.lastBaa = now;
    const pitch = 260 + Math.random() * 60;
    const osc = this.ctx.createOscillator();
    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(pitch, now);
    osc.frequency.linearRampToValueAtTime(pitch * 0.92, now + 0.4);
    // The bleat's wobble.
    const lfo = this.ctx.createOscillator();
    lfo.frequency.value = 9;
    const depth = this.ctx.createGain();
    depth.gain.value = pitch * 0.04;
    lfo.connect(depth).connect(osc.frequency);
    const formant = this.ctx.createBiquadFilter();
    formant.type = 'bandpass';
    formant.frequency.value = 1100;
    formant.Q.value = 1.2;
    const amp = this.envelope(0.05, 0.05, 0.38);
    osc.connect(formant).connect(amp).connect(this.out);
    for (const o of [osc, lfo]) {
      o.start(now);
      o.stop(now + 0.48);
    }
  }

  /**
   * Fuel glugging into the tank: three bubbly glugs rising as it fills, pitched in the mids so a
   * phone's little speaker carries them.
   */
  glug(): void {
    const now = this.ctx.currentTime;
    for (let i = 0; i < 3; i++) {
      const at = now + i * 0.12;
      const osc = this.ctx.createOscillator();
      osc.type = 'triangle';
      osc.frequency.setValueAtTime(360 + i * 60, at);
      osc.frequency.exponentialRampToValueAtTime(760 + i * 90, at + 0.09);
      const tone = this.ctx.createBiquadFilter();
      tone.type = 'lowpass';
      tone.frequency.value = 2400;
      const amp = this.envelope(0.1, 0.008, 0.1, at);
      osc.connect(tone).connect(amp).connect(this.out);
      osc.start(at);
      osc.stop(at + 0.13);
    }
  }

  /** A molehill flattened: a flumpy thump (in the mids, so a phone plays it) and loose earth. */
  plop(): void {
    const now = this.ctx.currentTime;
    const osc = this.ctx.createOscillator();
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(420, now);
    osc.frequency.exponentialRampToValueAtTime(120, now + 0.12);
    const tone = this.ctx.createBiquadFilter();
    tone.type = 'lowpass';
    tone.frequency.value = 1400;
    const thump = this.envelope(0.12, 0.004, 0.15);
    osc.connect(tone).connect(thump).connect(this.out);
    osc.start(now);
    osc.stop(now + 0.2);
    const src = this.noiseSource(false);
    src.playbackRate.value = 0.7;
    const band = this.ctx.createBiquadFilter();
    band.type = 'bandpass';
    band.Q.value = 0.9;
    band.frequency.setValueAtTime(1300, now);
    band.frequency.exponentialRampToValueAtTime(450, now + 0.18);
    const dirt = this.envelope(0.09, 0.01, 0.18);
    src.connect(band).connect(dirt).connect(this.out);
    src.start(now, Math.random(), 0.22);
  }

  /** A flat tire: a little pop, then air hissing out (or, with Hardened Tires, a light "tink"). */
  hiss(saved: boolean): void {
    const now = this.ctx.currentTime;
    if (saved) {
      const osc = this.ctx.createOscillator();
      osc.frequency.value = 2100;
      const amp = this.envelope(0.025, 0.002, 0.12);
      osc.connect(amp).connect(this.out);
      osc.start(now);
      osc.stop(now + 0.15);
      return;
    }
    const pop = this.noiseSource(false);
    const band = this.ctx.createBiquadFilter();
    band.type = 'bandpass';
    band.frequency.value = 1500;
    const popAmp = this.envelope(0.1, 0.001, 0.03);
    pop.connect(band).connect(popAmp).connect(this.out);
    pop.start(now, Math.random(), 0.05);
    const air = this.noiseSource(false);
    const high = this.ctx.createBiquadFilter();
    high.type = 'highpass';
    high.frequency.setValueAtTime(4200, now);
    high.frequency.exponentialRampToValueAtTime(2200, now + 0.7);
    const airAmp = this.envelope(0.065, 0.03, 0.7, now + 0.02);
    air.connect(high).connect(airAmp).connect(this.out);
    air.start(now + 0.02, Math.random(), 0.8);
  }

  /** A dead tree creaking: a slow, woody groan. */
  creak(): void {
    const now = this.ctx.currentTime;
    const osc = this.ctx.createOscillator();
    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(68, now);
    osc.frequency.linearRampToValueAtTime(92, now + 0.7);
    const lfo = this.ctx.createOscillator();
    lfo.frequency.value = 7;
    const depth = this.ctx.createGain();
    depth.gain.value = 6;
    lfo.connect(depth).connect(osc.frequency);
    const wood = this.ctx.createBiquadFilter();
    wood.type = 'bandpass';
    wood.frequency.value = 520;
    wood.Q.value = 3;
    const amp = this.envelope(0.12, 0.12, 0.65);
    osc.connect(wood).connect(amp).connect(this.out);
    for (const o of [osc, lfo]) {
      o.start(now);
      o.stop(now + 0.85);
    }
  }

  /**
   * The tree comes down: a heavy thud, and the crack and crash of branches (in the mids, so a
   * phone carries it, not only the low boom).
   */
  timber(): void {
    const now = this.ctx.currentTime;
    const osc = this.ctx.createOscillator();
    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(150, now);
    osc.frequency.exponentialRampToValueAtTime(55, now + 0.45);
    const body = this.ctx.createBiquadFilter();
    body.type = 'lowpass';
    body.frequency.value = 700;
    const boom = this.envelope(0.16, 0.006, 0.5);
    osc.connect(body).connect(boom).connect(this.out);
    osc.start(now);
    osc.stop(now + 0.6);
    const src = this.noiseSource(false);
    const band = this.ctx.createBiquadFilter();
    band.type = 'bandpass';
    band.Q.value = 0.7;
    band.frequency.setValueAtTime(1600, now);
    band.frequency.exponentialRampToValueAtTime(400, now + 0.5);
    const crash = this.envelope(0.16, 0.008, 0.5);
    src.connect(band).connect(crash).connect(this.out);
    src.start(now, Math.random(), 0.55);
    for (let i = 0; i < 6; i++) this.snapAt(now + 0.03 + Math.random() * 0.35, 0.14);
  }

  /** Sticks plowed under by a riding mower: a few quick, dry snaps (rate-limited). */
  snap(): void {
    const now = this.ctx.currentTime;
    if (now - this.lastSnap < 0.12) return;
    this.lastSnap = now;
    for (let i = 0; i < 3; i++) this.snapAt(now + i * (0.035 + Math.random() * 0.03), 0.22);
  }

  /** One twig snapping at `at`. */
  private snapAt(at: number, peak: number): void {
    const src = this.noiseSource(false);
    src.playbackRate.value = 1.3;
    const band = this.ctx.createBiquadFilter();
    band.type = 'bandpass';
    band.frequency.value = 1700 + Math.random() * 1100;
    band.Q.value = 1.6;
    const amp = this.envelope(peak, 0.001, 0.03, at);
    src.connect(band).connect(amp).connect(this.out);
    src.start(at, Math.random(), 0.05);
  }

  /** The mower comes apart: a metallic clang, then loose bits rattling down. */
  clang(): void {
    const now = this.ctx.currentTime;
    for (const [freq, decay] of [
      [420, 0.6],
      [1130, 0.42],
      [1890, 0.26],
    ] as const) {
      const osc = this.ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = freq;
      const amp = this.envelope(0.03, 0.002, decay);
      osc.connect(amp).connect(this.out);
      osc.start(now);
      osc.stop(now + decay + 0.05);
    }
    const src = this.noiseSource(false);
    const band = this.ctx.createBiquadFilter();
    band.type = 'bandpass';
    band.frequency.value = 3000;
    const crash = this.envelope(0.045, 0.002, 0.28);
    src.connect(band).connect(crash).connect(this.out);
    src.start(now, Math.random(), 0.32);
    // Bolts and bits rattling down after.
    for (let i = 0; i < 5; i++) this.snapAt(now + 0.18 + i * 0.07 + Math.random() * 0.04, 0.025);
  }

  /** A swarm boiling out of a smashed beehive: a short, angry buzz. */
  buzz(): void {
    const now = this.ctx.currentTime;
    const osc = this.ctx.createOscillator();
    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(190, now);
    osc.frequency.linearRampToValueAtTime(240, now + 0.5);
    osc.frequency.linearRampToValueAtTime(170, now + 1.1);
    const flutter = this.ctx.createOscillator();
    flutter.frequency.value = 26;
    const depth = this.ctx.createGain();
    depth.gain.value = 0.012;
    const band = this.ctx.createBiquadFilter();
    band.type = 'bandpass';
    band.frequency.value = 650;
    band.Q.value = 1.5;
    const amp = this.envelope(0.06, 0.08, 1.05);
    flutter.connect(depth).connect(amp.gain);
    osc.connect(band).connect(amp).connect(this.out);
    for (const o of [osc, flutter]) {
      o.start(now);
      o.stop(now + 1.2);
    }
  }

  /** Out of gas: the engine sputters out, a few slowing coughs. */
  sputter(): void {
    const now = this.ctx.currentTime;
    let at = now;
    for (let i = 0; i < 4; i++) {
      const osc = this.ctx.createOscillator();
      osc.type = 'sawtooth';
      osc.frequency.setValueAtTime(240 - i * 20, at);
      osc.frequency.exponentialRampToValueAtTime(110, at + 0.06);
      const amp = this.envelope(0.09 - i * 0.015, 0.003, 0.07, at);
      osc.connect(amp).connect(this.out);
      osc.start(at);
      osc.stop(at + 0.1);
      at += 0.12 + i * 0.07;
    }
  }

  /** Meadow grass growing back: a faint, rising whisper. */
  regrow(): void {
    const now = this.ctx.currentTime;
    const src = this.noiseSource(false);
    const band = this.ctx.createBiquadFilter();
    band.type = 'bandpass';
    band.Q.value = 1.4;
    band.frequency.setValueAtTime(600, now);
    band.frequency.exponentialRampToValueAtTime(2600, now + 0.45);
    const amp = this.envelope(0.035, 0.2, 0.3);
    src.connect(band).connect(amp).connect(this.out);
    src.start(now, Math.random(), 0.55);
  }

  /**
   * A soft, bell-like chime rising through a major chord: the reveal screens' fanfare (no music:
   * a few plucked notes). `big` adds the octave and a shimmer on top.
   */
  chime(big = false): void {
    const now = this.ctx.currentTime;
    const notes = big ? [523, 659, 784, 1047, 1319] : [659, 784, 1047];
    notes.forEach((f, i) => {
      const at = now + i * 0.09;
      for (const [mult, peak] of [
        [1, 0.05],
        [2.01, 0.015],
      ] as const) {
        const osc = this.ctx.createOscillator();
        osc.type = 'sine';
        osc.frequency.value = f * mult;
        const amp = this.envelope(peak, 0.005, 0.7, at);
        osc.connect(amp).connect(this.out);
        osc.start(at);
        osc.stop(at + 0.75);
      }
    });
    if (big) {
      const src = this.noiseSource(false);
      const band = this.ctx.createBiquadFilter();
      band.type = 'highpass';
      band.frequency.value = 6000;
      const amp = this.envelope(0.012, 0.2, 0.9, now + 0.3);
      src.connect(band).connect(amp).connect(this.out);
      src.start(now + 0.3, Math.random(), 1.2);
    }
  }

  /**
   * Lawn Art: a soft bell for a picture tile mowed right, stepping up a pentatonic scale as the
   * picture comes together (`step` counts them); a low, soft boop for one mowed the wrong way.
   */
  artTile(right: boolean, step: number): void {
    const now = this.ctx.currentTime;
    const osc = this.ctx.createOscillator();
    if (right) {
      const scale = [523, 587, 659, 784, 880, 1047, 1175, 1319, 1568, 1760];
      osc.type = 'sine';
      osc.frequency.value = scale[step % scale.length]!;
      const amp = this.envelope(0.03, 0.004, 0.22);
      osc.connect(amp).connect(this.out);
      osc.start(now);
      osc.stop(now + 0.26);
      return;
    }
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(260, now);
    osc.frequency.exponentialRampToValueAtTime(170, now + 0.14);
    const amp = this.envelope(0.05, 0.006, 0.16);
    osc.connect(amp).connect(this.out);
    osc.start(now);
    osc.stop(now + 0.2);
  }

  /** A tiny tick when the mower turns. */
  tick(): void {
    const now = this.ctx.currentTime;
    const osc = this.ctx.createOscillator();
    osc.frequency.value = 1400;
    const amp = this.envelope(0.008, 0.002, 0.03);
    osc.connect(amp).connect(this.out);
    osc.start(now);
    osc.stop(now + 0.05);
  }

  private noiseSource(loop: boolean): AudioBufferSourceNode {
    const src = this.ctx.createBufferSource();
    src.buffer = this.noise;
    src.loop = loop;
    return src;
  }

  /** A gain node with a quick attack/decay envelope starting at `at`. */
  private envelope(
    peak: number,
    attack: number,
    decay: number,
    at = this.ctx.currentTime,
  ): GainNode {
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(0, at);
    g.gain.linearRampToValueAtTime(peak, at + attack);
    g.gain.exponentialRampToValueAtTime(0.0001, at + attack + decay);
    return g;
  }

  private fadeOut(amp: GainNode, sources: (AudioScheduledSourceNode | OscillatorNode)[]): void {
    const t = this.ctx.currentTime;
    amp.gain.cancelScheduledValues(t);
    amp.gain.setTargetAtTime(0, t, 0.08);
    for (const s of sources) s.stop(t + 0.5);
  }
}
